import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    LEGAL_AGREEMENT_STATUS_OPTIONS,
    LegalAgreementStatus,
    type CreateLegalAgreementPayload,
    type LegalAgreement,
    type SelectOption,
    type SupersedeLegalAgreementPayload,
    type UpdateLegalAgreementPayload,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

// Valor do formulário de criação de acordo (sem status — nasce em formalização).
interface AddLegalAgreementValue extends CreateLegalAgreementPayload {}

// Gerencia os acordos de um caso: lista, adiciona, edita campos, muda status no ciclo,
// renegocia (superseção) e remove. Fetch próprio pelo id do caso (a seção atualiza sem
// recarregar o page-view inteiro). Mesmo padrão do useLegalCaseSeizedAssets (seededCaseRef
// re-seed por caso, opções de status vindas do page-form com fallback local).
export default function useLegalCaseAgreements(
    caseId: string | null,
    initialAgreements?: LegalAgreement[],
) {
    const [agreements, setAgreements] = useState<LegalAgreement[]>(initialAgreements || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [statusOptions, setStatusOptions] = useState<SelectOption[]>(
        LEGAL_AGREEMENT_STATUS_OPTIONS,
    );
    const seededCaseRef = useRef<string | null>(null);
    // Geração de request: guarda contra resposta obsoleta. O fetch do mount pode chegar depois
    // de uma mutação (add/status/supersede/remove) e sobrescrever a lista com o snapshot antigo
    // — o mesmo "sumiço" que o re-fetch veio corrigir, agora por corrida. Cada fetch captura a
    // geração ao iniciar e só aplica o resultado se ainda for a corrente; toda mutação
    // incrementa a geração, invalidando qualquer fetch em voo.
    const fetchGenRef = useRef(0);

    const fetchAgreements = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getAgreements(caseId);
            if (fetchGenRef.current === generation) setAgreements(result.items || []);
        } catch {
            // silencioso — a seção já mostra os acordos que vieram no page-view
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setAgreements(initialAgreements || []);
        setErrorFields([]);
        // Semeia com o que veio no page-view (paint imediato) e re-busca do servidor. O
        // re-fetch cobre o caso de a seção remontar (ao alternar de aba na página de detalhe):
        // sem ele, a remontagem voltaria ao `initialAgreements` do page-view — carregado antes
        // de qualquer inclusão nesta sessão — e o registro recém-criado "sumiria" até recarregar
        // a página. initialAgreements fora das deps de propósito (referência nova a cada render).
        fetchAgreements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        const loadStatuses = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.legalAgreementStatuses?.length) {
                    setStatusOptions(opts.legalAgreementStatuses);
                }
            } catch {
                // fallback local já preenche
            }
        };
        loadStatuses();
    }, []);

    const clearErrors = () => setErrorFields([]);

    const addAgreement = async (data: AddLegalAgreementValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addAgreement(caseId, data);
            await fetchAgreements();
            toast.success('Acordo registrado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao registrar acordo');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const updateAgreement = async (
        agreementId: string,
        data: UpdateLegalAgreementPayload,
    ): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.updateAgreement(caseId, agreementId, data);
            await fetchAgreements();
            toast.success('Acordo atualizado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar acordo');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const changeStatus = async (agreementId: string, status: LegalAgreementStatus) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.updateAgreementStatus(caseId, agreementId, status);
            await fetchAgreements();
            toast.success('Status atualizado');
        } catch (e: any) {
            // 422 = transição inválida; o backend explica no message.
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar status');
        } finally {
            setIsSaving(false);
        }
    };

    const supersedeAgreement = async (
        agreementId: string,
        data: SupersedeLegalAgreementPayload,
    ): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.supersedeAgreement(caseId, agreementId, data);
            await fetchAgreements();
            toast.success('Acordo renegociado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao renegociar acordo');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeAgreement = async (agreementId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeAgreement(caseId, agreementId);
            await fetchAgreements();
            toast.success('Acordo removido');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover acordo');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        agreements, isLoading, isSaving, errorFields, statusOptions,
        addAgreement, updateAgreement, changeStatus, supersedeAgreement, removeAgreement, clearErrors,
    };
}

export type { AddLegalAgreementValue };
